import type { SaveEventTravelRequest } from './travel';
export interface Event {
  id: number;
  title: string;
  description?: string;
  type: EventType;
  startTime: string;
  endTime: string;
  location?: string;
  ageGroup: string;
  ageGroupId?: number;
  /** Korosztály helyett vendég csapat neve. A kettő kizárja egymást. */
  guestTeam?: string | null;
  isRecurring: boolean;
  recurrenceType?: RecurrenceType;
  recurrenceInterval?: number;
  recurrenceEndDate?: string;
  responsesEnabled: boolean;
  /** Elmarad (lemondták). */
  isCancelled?: boolean;
  excuseLetterEnabled: boolean;
  /** Küldjön-e az app push értesítést az eseményről. */
  notificationsEnabled: boolean;
  /** Menjen-e azonnali értesítés az esemény létrehozásakor. */
  notifyOnCreate: boolean;
  /** A létrehozó is kapjon értesítést az eseményről. */
  notifyCreator: boolean;
  reminders?: EventReminder[];
  createdAt: string;
  createdByUserId: number;
  venueId?: number;
  venueSections?: string | null;
  commentCount?: number;
  createdByUser?: {
    id: number;
    name: string;
    email: string;
  };
  venue?: {
    id: number;
    name: string;
    address?: string;
    sections?: number;
    /** A pályarészek nevei (név híján "N. rész"). */
    sectionNames?: string[];
  };
  attendances?: EventAttendance[];
  responseSummary?: EventResponseSummary;
}

/**
 * Egy beállított emlékeztető. Kétféle mód:
 *  - relatív: offsetMinutes perccel az esemény kezdete előtt,
 *  - abszolút: daysBefore nappal korábban, timeOfDayMinutes időpontban.
 */
export interface EventReminderInput {
  offsetMinutes: number;
  daysBefore?: number | null;
  timeOfDayMinutes?: number | null;
}

export interface EventReminder extends EventReminderInput {
  id: number;
  /** A kiszámított küldési időpont (klub helyi idő). */
  scheduledAt: string;
  /** Null, ha még nem ment ki. */
  sentAt: string | null;
}

// Updated enum values to match backend
export enum EventType {
  Training = 0,
  Match = 1,
  Meeting = 2,
  Other = 3,
}

export enum RecurrenceType {
  Daily = 1,
  Weekly = 2,
  Monthly = 3,
}

export interface EventAttendance {
  id: number;
  status: AttendanceStatus;
  notes?: string;
  createdAt: string;
  eventId: number;
  userId: number;
  playerId?: number;
  user?: {
    id: number;
    name: string;
    email: string;
  };
  player?: {
    id: number;
    name: string;
  };
}

// Updated enum values to match backend
export enum AttendanceStatus {
  NoResponse = 0,
  Attending = 1,
  NotAttending = 2,
  Maybe = 3,
}

export interface EventManualReminderResult {
  playerCount: number;
  userCount: number;
  delivered: number;
  pushNotConfigured: boolean;
}

export interface EventResponseSummary {
  attendingCount: number;
  notAttendingCount: number;
  maybeCount: number;
  noResponseCount: number;
  totalCount: number;
}

export interface EventResponse {
  id: number;
  eventId: number;
  userId: number;
  userName: string;
  playerId?: number;
  playerName?: string;
  status: AttendanceStatus;
  notes?: string;
  createdAt: string;
}

export interface SubmitResponseRequest {
  status: AttendanceStatus;
  playerId?: number;
  notes?: string;
}

export interface EventComment {
  id: number;
  content: string;
  createdAt: string;
  updatedAt?: string;
  eventId: number;
  userId: number;
  userName: string;
}

export interface CreateCommentRequest {
  content: string;
}

export interface UpdateCommentRequest {
  content: string;
}

export interface CreateEventRequest {
  title: string;
  description?: string;
  type: EventType;
  startTime: string;
  endTime: string;
  location?: string;
  ageGroup: string;
  ageGroupId?: number;
  /** Korosztály helyett vendég csapat neve. A kettő kizárja egymást. */
  guestTeam?: string;
  isRecurring: boolean;
  recurrenceType?: RecurrenceType;
  recurrenceInterval?: number;
  recurrenceEndDate?: string;
  venueId?: number;
  venueSections?: string;
  responsesEnabled?: boolean;
  excuseLetterEnabled?: boolean;
  notificationsEnabled?: boolean;
  notifyOnCreate?: boolean;
  notifyCreator?: boolean;
  /** Ha nincs megadva, a klub alapértelmezett emlékeztetői kerülnek be. */
  reminders?: EventReminderInput[];
  /** Mérkőzésnél: futsal (4+1 fős felállás). */
  isFutsal?: boolean;
  /** Mérkőzésnél az opcionális keret: csak ők jelezhetnek vissza. */
  squadPlayerIds?: number[];
  /** Utazás az eseménnyel együtt (járművek, megállók); ismétlődésnél minden alkalomra. */
  travel?: SaveEventTravelRequest;
}

export interface UpdateEventRequest {
  title?: string;
  description?: string;
  type?: EventType;
  startTime?: string;
  endTime?: string;
  location?: string;
  ageGroup?: string;
  ageGroupId?: number;
  /** Vendég csapat neve; üres string = visszaváltás korosztályra. */
  guestTeam?: string;
  isRecurring?: boolean;
  recurrenceType?: RecurrenceType;
  recurrenceInterval?: number;
  recurrenceEndDate?: string;
  venueId?: number;
  venueSections?: string;
  responsesEnabled?: boolean;
  excuseLetterEnabled?: boolean;
  notificationsEnabled?: boolean;
  notifyOnCreate?: boolean;
  notifyCreator?: boolean;
  /** Ha nincs megadva, az emlékeztetők változatlanok maradnak. */
  reminders?: EventReminderInput[];
  /** Mérkőzésről más típusra váltásnál: true = a mérkőzés rekord is törlődjön */
  deleteMatch?: boolean;
  /** Mérkőzésnél: futsal-e (nincs megadva = változatlan). */
  isFutsal?: boolean;
}

export interface CalendarView {
  month: string;
  week: string;
  day: string;
}

export interface CalendarEvent extends Event {
  color?: string;
  isAllDay?: boolean;
  duration?: number;
}

// Excuse Letter types
export interface ExcuseLetterRequest {
  documentId: number;
  playerId: number;
  playerName: string;
  requestedAt: string;
  isGenerated: boolean;
  /** Mikor ment ki a játékos iskolájának. */
  sentToSchoolAt?: string | null;
}

// Helper function to get event type label
export const getEventTypeLabel = (type: EventType): string => {
  switch (type) {
    case EventType.Training:
      return 'Edzés';
    case EventType.Match:
      return 'Mérkőzés';
    case EventType.Meeting:
      return 'Megbeszélés';
    case EventType.Other:
      return 'Egyéb';
    default:
      return 'Ismeretlen';
  }
};

// Helper function to get attendance status label
export const getAttendanceStatusLabel = (status: AttendanceStatus): string => {
  switch (status) {
    case AttendanceStatus.Attending:
      return 'Részt vesz';
    case AttendanceStatus.NotAttending:
      return 'Nem vesz részt';
    case AttendanceStatus.NoResponse:
      return 'Nincs válasz';
    default:
      return 'Ismeretlen';
  }
};

/**
 * Az esemény csoport-címkéje: vendég csapatos eseménynél a vendég csapat neve,
 * egyébként a korosztály. Mindenhol ezt írjuk ki, ahol eddig a korosztály
 * jelent meg (naptár zárójeles kiegészítés, badge, tooltip).
 */
export const eventGroupLabel = (
  event: { ageGroup?: string; guestTeam?: string | null } | null | undefined
): string => (event ? event.guestTeam || event.ageGroup || '' : '');
